import { useMemo, useState } from "react";
import { Alert, AlertDescription, Skeleton, Stack } from "@altimateai/lego";
import AutoTuneHistoryCard from "@/pages/jobs/auto-tune/AutoTuneHistoryCard";
import AutoTuneOverviewCards from "@/pages/jobs/auto-tune/AutoTuneOverviewCards";
import type { AutoTuneSavingsTrendPoint } from "@/pages/jobs/auto-tune/types";
import ClusterAutoTuneSavingsCard from "./ClusterAutoTuneSavingsCard";
import {
  useClusterAutoTuneHistory,
  useClusterAutoTuneRecommendations,
  useClusterAutoTuneSavingsTrends,
} from "./hooks";

interface Props {
  clusterId: string;
  instanceId?: number | string;
  workspaceId?: string;
  // Calendar-filter bounds (YYYY-MM-DD) from the cluster-detail page,
  // threaded to the savings chart so it honors the page date range.
  startDate?: string;
  endDate?: string;
}

const PAGE_SIZE = 10;

// Cluster Auto Tune tab body: overview tiles (recommendations enabled +
// potential savings), the daily Cost Savings chart, and the audit
// history. Mirrors the jobs ``AutoTunePerformancePanel`` but with no
// realized/actual savings (clusters have no realized mart) and counts
// over recommendations rather than job tasks.
const ClusterAutoTunePerformancePanel = ({
  clusterId,
  instanceId,
  workspaceId,
  startDate,
  endDate,
}: Props) => {
  const [historyPage, setHistoryPage] = useState(1);

  const recommendations = useClusterAutoTuneRecommendations(clusterId, instanceId, workspaceId);
  const history = useClusterAutoTuneHistory(clusterId, {
    page: historyPage,
    page_size: PAGE_SIZE,
    instance_id: instanceId,
    workspace_id: workspaceId,
  });

  const recs = recommendations.data?.recommendations ?? [];
  const tasksTotal = recs.length;
  const tasksEnabled = recs.filter(rec => rec.enabled).length;

  const hasDateRange = Boolean(startDate && endDate);
  const savingsTrends = useClusterAutoTuneSavingsTrends(
    clusterId,
    instanceId,
    startDate ?? "",
    endDate ?? "",
    hasDateRange
  );
  const potentialTotal = useMemo(() => {
    if (!savingsTrends.data) return undefined;
    const total = (points: AutoTuneSavingsTrendPoint[]) =>
      points.reduce((sum, p) => sum + p.dbu_savings + p.infra_savings, 0);
    return total(savingsTrends.data.potential);
  }, [savingsTrends.data]);

  const historyItems = history.data?.items ?? [];
  const historyTotal = history.data?.total_count ?? 0;
  const historyPages = history.data?.total_pages ?? 1;

  if (recommendations.isLoading) {
    return (
      <Stack direction="column" gap="lg" aria-label="Auto Tune" aria-busy="true">
        <div className="al-grid al-grid-cols-2 lg:al-grid-cols-4 al-gap-4">
          <Skeleton className="al-h-[88px] al-w-full" />
          <Skeleton className="al-h-[88px] al-w-full" />
        </div>
        <Skeleton className="al-h-40 al-w-full" />
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap="lg" aria-label="Auto Tune">
      {tasksTotal > 0 ? (
        <AutoTuneOverviewCards
          tasksLabel="Recommendations"
          tasksEnabled={tasksEnabled}
          tasksTotal={tasksTotal}
          potentialSavingsUsd={potentialTotal}
        />
      ) : null}

      <ClusterAutoTuneSavingsCard
        clusterId={clusterId}
        instanceId={instanceId}
        startDate={startDate}
        endDate={endDate}
      />

      {history.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Failed to load Auto tune history. Try again later.</AlertDescription>
        </Alert>
      ) : (
        <AutoTuneHistoryCard
          events={historyItems}
          total={historyTotal}
          page={historyPage}
          pageSize={PAGE_SIZE}
          totalPages={historyPages}
          onPageChange={setHistoryPage}
          isLoading={history.isLoading}
        />
      )}
    </Stack>
  );
};

export default ClusterAutoTunePerformancePanel;
